import { Chevron } from "@components/icons"
import ImageHelper, {
  Container as ImageHelperContainer,
} from "@components/ImageHelper"
import MuxVideo from "@components/MuxVideo"
import Video from "@components/Video"
import animation, { cubicBezier } from "@constants/animation"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import {
  DesktopOnlyContainer,
  MobileAndTabletContainer,
} from "@utils/MediaQuery"
import { emptyRichText } from "@utils/richText"
import { Link } from "gatsby"
import { renderRichText } from "gatsby-source-contentful/rich-text"
import React, { useRef, useState } from "react"
import { SwipeableProps, useSwipeable } from "react-swipeable"
import styled, { css } from "styled-components"

import Item from "./Item"

const LIVE_TEXT_X_ORIENTATION_LEFT = "LEFT"
const LIVE_TEXT_X_ORIENTATION_CENTER = "CENTER"
const LIVE_TEXT_X_ORIENTATION_RIGHT = "RIGHT"
const liveTextXOrientationMap = {
  [LIVE_TEXT_X_ORIENTATION_CENTER]: "center",
  [LIVE_TEXT_X_ORIENTATION_LEFT]: "flex-start",
  [LIVE_TEXT_X_ORIENTATION_RIGHT]: "flex-end",
}
const liveTextTextAlignmentMap = {
  [LIVE_TEXT_X_ORIENTATION_CENTER]: "center",
  [LIVE_TEXT_X_ORIENTATION_LEFT]: "start",
  [LIVE_TEXT_X_ORIENTATION_RIGHT]: "end",
}

const LIVE_TEXT_Y_ORIENTATION_TOP = "TOP"
const LIVE_TEXT_Y_ORIENTATION_CENTER = "CENTER"
const LIVE_TEXT_Y_ORIENTATION_BOTTOM = "BOTTOM"
const liveTextYOrientationMap = {
  [LIVE_TEXT_Y_ORIENTATION_BOTTOM]: "flex-end",
  [LIVE_TEXT_Y_ORIENTATION_CENTER]: "center",
  [LIVE_TEXT_Y_ORIENTATION_TOP]: "flex-start",
}
const liveTextYOrientationPaddingMap = {
  [LIVE_TEXT_Y_ORIENTATION_BOTTOM]:
    "calc(0.5 / 12 * 100vw) calc(0.5 / 12 * 100vw) calc(1.5 / 12 * 100vw + 48px)",
  [LIVE_TEXT_Y_ORIENTATION_CENTER]: "calc(0.5 / 12 * 100vw)",
  [LIVE_TEXT_Y_ORIENTATION_TOP]: "calc(0.5 / 12 * 100vw)",
}

const Container = styled.div`
  flex-direction: column;
  position: relative;
  display: flex;
  height: calc(((100vh - 56px) - 24px) - 48px);
  overflow: hidden;
  margin-bottom: 80px;

  @media ${breakpoints.tablet} {
    height: calc(((100vh - 72px) - 24px) - 48px);
  }

  @media ${breakpoints.laptop} {
    height: calc(((100vh - 88px) - 24px) - 36px);
    margin-bottom: calc(0.5 / 12 * 100vw);
  }
`

const MediaContainer = styled.div`
  display: flex;
  position: relative;
  height: 100%;
  width: 100%;
  flex-direction: column;
  background-color: ${colors.black};
`

const MediaGradient = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #000000 100%);
  opacity: 0.05;
`

const HideControlsClickContainer = styled.div`
  align-items: center;
  justify-content: space-between;
  display: flex;
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 1;
`

const HideControlsExternalLinkButton = styled.a`
  width: 100%;
  height: 100%;

  &:hover {
    cursor: pointer;
  }
`

const HideControlsLinkButton = styled(Link)`
  width: 100%;
  height: 100%;

  &:hover {
    cursor: pointer;
  }
`

const ContentContainer = styled.div`
  display: flex;
  position: absolute;
  bottom: 28px;
  left: 0;
  right: 0;
  padding: 0 calc(0.5 / 12 * 100vw) calc(0.5 / 12 * 100vw + 48px);
  flex-direction: row;
  align-items: center;
  justify-content: center;

  @media ${breakpoints.tablet} {
    bottom: 0;
  }

  @media ${breakpoints.laptop} {
    bottom: 0;
    padding-bottom: calc(0.5 / 12 * 100vw);
  }
`

const ClickOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 5;
  display: flex;
  width: calc(100vw - (1 / 12 * 100vw));
  height: calc(100% - ((0.5 / 12 * 100vw) + 86px));
  margin: auto;
`

const LiveText = styled.div``

const LiveTextContainer = styled.div<{
  liveTextFontColor: string
  liveTextHeaderFontColor: string
  liveTextXOrientation: string
  liveTextYOrientation: string
  mobileLiveTextFontColor: string
  mobileLiveTextHeaderFontColor: string
  mobileLiveTextXOrientation: string
  mobileLiveTextYOrientation: string
}>`
  ${({
    liveTextFontColor,
    liveTextHeaderFontColor,
    liveTextXOrientation,
    liveTextYOrientation,
    mobileLiveTextFontColor,
    mobileLiveTextHeaderFontColor,
    mobileLiveTextXOrientation,
    mobileLiveTextYOrientation,
  }) => css`
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    align-items: ${liveTextYOrientationMap[mobileLiveTextYOrientation]};
    justify-content: ${liveTextXOrientationMap[mobileLiveTextXOrientation]};
    display: flex;
    width: 100%;
    height: 100%;
    padding: ${liveTextYOrientationPaddingMap[mobileLiveTextYOrientation]};

    @media ${breakpoints.laptop} {
      align-items: ${liveTextYOrientationMap[liveTextYOrientation]};
      justify-content: ${liveTextXOrientationMap[liveTextXOrientation]};
      padding: calc(0.5 / 12 * 100vw);
    }

    ${LiveText} {
      text-align: ${liveTextTextAlignmentMap[mobileLiveTextXOrientation]};
      color: #${mobileLiveTextFontColor};

      h1,
      h2,
      h3,
      h4 {
        color: #${mobileLiveTextHeaderFontColor};
      }

      h1,
      h2,
      h3,
      h4,
      p {
        margin: 12px;
      }

      h1 {
        font-family: "Interstate Bold", Helvetica, Arial, sans-serif;
        font-size: 40px;
      }

      h2 {
        font-family: "Interstate Bold", Helvetica, Arial, sans-serif;
        font-size: 32px;
      }

      h3 {
        font-family: "Interstate Bold", Helvetica, Arial, sans-serif;
        font-size: 24px;
      }

      h4 {
        font-family: "Interstate Bold", Helvetica, Arial, sans-serif;
        font-size: 18px;
      }

      p {
        min-height: 24px;
        font-family: "Avenir Roman", Helvetica, Arial, sans-serif;
        font-size: 14px;
        line-height: 1.2em;
      }

      b {
        font-weight: 800;
      }

      @media ${breakpoints.laptop} {
        color: #${liveTextFontColor};
        text-align: ${liveTextTextAlignmentMap[liveTextXOrientation]};

        h1,
        h2,
        h3,
        h4 {
          color: #${liveTextHeaderFontColor};
        }

        h1,
        h2,
        h3,
        h4,
        p {
          margin: 12px;
        }

        h1 {
          font-family: "Interstate Bold", Helvetica, Arial, sans-serif;
          font-size: 48px;
        }

        h2 {
          font-family: "Interstate Bold", Helvetica, Arial, sans-serif;
          font-size: 48px;
        }

        h3 {
          font-family: "Interstate Bold", Helvetica, Arial, sans-serif;
          font-size: 48px;
        }

        h4 {
          font-family: "Interstate Bold", Helvetica, Arial, sans-serif;
          font-size: 32px;
        }

        p {
          min-height: 30px;
          font-family: "Avenir Roman", Helvetica, Arial, sans-serif;
          font-size: 20px;
          line-height: 1.2em;
        }

        b {
          font-weight: 800;
        }
      }
    }
  `}
`

const Image = styled.div`
  height: 100%;
  width: 100%;

  ${ImageHelperContainer} {
    height: 100%;
    width: 100%;
    transition: opacity ${animation.long} ${cubicBezier};
  }
`

const PrevButton = styled.div`
  cursor: pointer;
  position: absolute;
  left: 0;
  top: 50%;
  z-index: 10;
  width: 64px;
  height: 64px;
  transform: translateY(-50%) rotate(180deg);

  @media ${breakpoints.laptop} {
    z-index: 1;
  }
`

const NextButton = styled.div`
  cursor: pointer;
  position: absolute;
  right: 0;
  top: 50%;
  z-index: 10;
  width: 64px;
  height: 64px;
  transform: translateY(-50%);

  @media ${breakpoints.laptop} {
    z-index: 1;
  }
`

const Dots = styled.div`
  position: absolute;
  bottom: 20px;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;

  @media ${breakpoints.laptop} {
    bottom: calc(0.5 / 12 * 100vw);
  }
`

const Dot = styled.div<{ active?: boolean }>`
  width: 8px;
  height: 8px;
  margin: 0 8px;
  border-radius: 50%;
  background-color: ${({ active }) =>
    active ? colors.black : colors.offWhite};

  @media ${breakpoints.laptop} {
    &:hover {
      cursor: ${({ active }) => (!active ? "pointer" : "initial")};
    }
  }
`

enum CarouselState {
  Initial,
  InitImage,
  Playing,
  InitVideo,
}

interface MediaCarouselProps {
  blockId?: string
  hideControls?: boolean
  items: Array<{
    externalLink?: boolean
    id: string
    image?: string
    imageDuration?: number
    link: string
    liveRichText?: any
    liveTextFontColor?: string
    liveTextHeaderFontColor?: string
    liveTextXOrientation?: string
    liveTextYOrientation?: string
    mobileImage?: string
    mobileLiveRichText?: any
    mobileLiveTextFontColor?: string
    mobileLiveTextHeaderFontColor?: string
    mobileLiveTextXOrientation?: string
    mobileLiveTextYOrientation?: string
    mobileVideoUrl?: string
    muxMobileVideo?: {
      title: string
      video: {
        assetId: string
        audioOnly: boolean
        created_at: number
        duration: number
        max_stored_frame_rate: number
        max_stored_resolution: string
        playbackId: string
        ratio: string
        ready: boolean
        version: number
      }
    }
    muxVideo?: {
      title: string
      video: {
        assetId: string
        audioOnly: boolean
        created_at: number
        duration: number
        max_stored_frame_rate: number
        max_stored_resolution: string
        playbackId: string
        ratio: string
        ready: boolean
        version: number
      }
    }
    title: string
    videoThumbnail?: string
    videoUrl?: string
  }>
  swipeConfig?: SwipeableProps
}

function MediaCarousel(props: MediaCarouselProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [activeItemIndex, setActiveItemIndex] = useState(0)
  const activeItem = props.items[activeItemIndex]
  const [progressDuration, setProgressDuration] = useState(
    activeItem.imageDuration || 30
  )
  const [carouselState, setCarouselState] = useState(CarouselState.Initial)
  const timeoutRefMobile = React.useRef<any>(undefined)
  const timeoutRefDesktop = React.useRef<any>(undefined)
  const handlers = useSwipeable({
    onSwiped: (eventData) => {
      switch (eventData.dir) {
        case "Left":
          onNext()
          break
        case "Right":
          onPrev()
          break
      }
    },
    ...props.swipeConfig,
  })

  const [opacity, setOpacity] = useState(1)

  const onMuxVideoPlay = (isMobile: boolean) => {
    const duration = isMobile
      ? activeItem?.muxMobileVideo?.video.duration ||
        activeItem?.muxVideo?.video.duration
      : activeItem?.muxVideo?.video.duration
    if (duration) {
      setProgressDuration(duration)
      setCarouselState(CarouselState.Playing)
    }
  }

  const playVideo = () => {
    const videoElement = videoRef.current
    if (videoElement) {
      const duration = videoElement.duration
      setProgressDuration(duration)
      setCarouselState(CarouselState.Playing)
      const playbackStarted = videoElement.play()
      if (playbackStarted !== undefined) {
        playbackStarted.catch((error) => {
          // autoPlay not allowed or not supported
        })
      }
    }
  }

  const playSlide = (nextIndex) => {
    if (timeoutRefMobile.current) {
      clearTimeout(timeoutRefMobile.current)
      timeoutRefMobile.current = undefined
    }
    if (timeoutRefDesktop.current) {
      clearTimeout(timeoutRefDesktop.current)
      timeoutRefDesktop.current = undefined
    }
    const nextItem = props.items[nextIndex]
    if (nextItem.videoUrl) {
      setCarouselState(CarouselState.InitVideo)
    } else {
      setCarouselState(CarouselState.InitImage)
    }
    setActiveItemIndex(nextIndex)
    setTimeout(() => {
      setOpacity(1)
    }, 100)
  }

  const onPrev = () => {
    setOpacity(0.3)
    const prevItemIndex =
      activeItemIndex - 1 < 0 ? props.items.length - 1 : activeItemIndex - 1
    playSlide(prevItemIndex)
  }

  const onNext = (isImage?) => {
    if (
      isImage &&
      activeItemIndex > 0 &&
      !(timeoutRefMobile.current && timeoutRefDesktop.current)
    ) {
      return
    }
    setOpacity(0.3)
    const nextItemIndex =
      activeItemIndex + 1 < props.items.length ? activeItemIndex + 1 : 0
    playSlide(nextItemIndex)
  }

  const onVideoEnded = () => {
    onNext()
  }

  const [firstMobileImageInit, setFirstMobileImageInit] =
    React.useState<boolean>(false)
  const [firstDesktopImageInit, setFirstDesktopImageInit] =
    React.useState<boolean>(false)

  const onImageLoadMobile = () => {
    const duration = activeItem.imageDuration || 30
    setProgressDuration(duration)
    setCarouselState(CarouselState.Playing)
    timeoutRefMobile.current = setTimeout(() => {
      onNext(true)
    }, duration * 1000)
  }

  const onImageLoadDesktop = () => {
    const duration = activeItem.imageDuration || 30
    setProgressDuration(duration)
    setCarouselState(CarouselState.Playing)
    timeoutRefDesktop.current = setTimeout(() => {
      onNext(true)
    }, duration * 1000)
  }

  React.useEffect(() => {
    if (props.items && props.items[0] && !props.items[0].image) {
      setFirstMobileImageInit(true)
      setFirstDesktopImageInit(true)
      return
    }

    if (
      !firstMobileImageInit &&
      props.items &&
      props.items[0] &&
      props.items[0].image
    ) {
      const duration = activeItem.imageDuration || 30
      setProgressDuration(duration)
      setCarouselState(CarouselState.Playing)
      setFirstMobileImageInit(true)
      timeoutRefMobile.current = setTimeout(() => {
        onNext(true)
      }, duration * 1000)
    }

    if (
      !firstDesktopImageInit &&
      props.items &&
      props.items[0] &&
      props.items[0].image
    ) {
      const duration = activeItem.imageDuration || 30
      setProgressDuration(duration)
      setCarouselState(CarouselState.Playing)
      setFirstDesktopImageInit(true)
      timeoutRefDesktop.current = setTimeout(() => {
        onNext(true)
      }, duration * 1000)
    }
  }, [])

  const onCanPlayThrough = () => {
    playVideo()
  }

  const formatLink = (link: string): string => {
    if (!link) {
      return "/"
    }

    const baseURL = process.env.GATSBY_BASE_URL
    return link[0] === "/" ? `${baseURL}${link}` : `${baseURL}/${link}`
  }

  return (
    <Container {...handlers} id={props.blockId || undefined}>
      <MediaContainer>
        {props.hideControls && (
          <HideControlsClickContainer>
            {activeItem.link && activeItem.externalLink ? (
              <HideControlsExternalLinkButton
                href={activeItem.link}
                rel="noopener"
                target="_blank"
              />
            ) : (
              <HideControlsLinkButton to={formatLink(activeItem.link)} />
            )}
          </HideControlsClickContainer>
        )}
        {!props.hideControls && (
          <Link to={formatLink(activeItem.link)}>
            <ClickOverlay />
          </Link>
        )}
        {activeItem.liveRichText && (
          <LiveTextContainer
            liveTextFontColor={activeItem.liveTextFontColor || colors.offWhite}
            liveTextHeaderFontColor={
              activeItem.liveTextHeaderFontColor ||
              activeItem.liveTextFontColor ||
              colors.offWhite
            }
            liveTextXOrientation={
              activeItem.liveTextXOrientation || LIVE_TEXT_X_ORIENTATION_CENTER
            }
            liveTextYOrientation={
              activeItem.liveTextYOrientation || LIVE_TEXT_Y_ORIENTATION_CENTER
            }
            mobileLiveTextFontColor={
              activeItem.mobileLiveTextFontColor ||
              activeItem.liveTextFontColor ||
              colors.offWhite
            }
            mobileLiveTextHeaderFontColor={
              activeItem.mobileLiveTextHeaderFontColor ||
              activeItem.mobileLiveTextFontColor ||
              activeItem.liveTextFontColor ||
              colors.offWhite
            }
            mobileLiveTextXOrientation={
              activeItem.mobileLiveTextXOrientation ||
              activeItem.liveTextXOrientation ||
              LIVE_TEXT_X_ORIENTATION_CENTER
            }
            mobileLiveTextYOrientation={
              activeItem.mobileLiveTextYOrientation ||
              activeItem.liveTextYOrientation ||
              LIVE_TEXT_Y_ORIENTATION_CENTER
            }
          >
            <LiveText>
              <MobileAndTabletContainer>
                {renderRichText(
                  activeItem.mobileLiveRichText ||
                    activeItem.liveRichText ||
                    emptyRichText
                )}
              </MobileAndTabletContainer>
              <DesktopOnlyContainer>
                {renderRichText(activeItem.liveRichText || emptyRichText)}
              </DesktopOnlyContainer>
            </LiveText>
          </LiveTextContainer>
        )}
        {activeItem.muxVideo || activeItem.muxMobileVideo ? (
          <MuxVideo
            hideControls={true}
            key={activeItem.id}
            loopVideo={false}
            muxMobileVideo={activeItem.muxMobileVideo}
            muxVideo={activeItem.muxVideo}
            onEnded={onVideoEnded}
            onPlaying={onMuxVideoPlay}
          />
        ) : activeItem.videoUrl ? (
          <Video
            autoPlay={true}
            controls={false}
            disableClickToPause
            key={activeItem.id}
            mobileSrc={activeItem.mobileVideoUrl}
            muted={true}
            onCanPlayThrough={onCanPlayThrough}
            onEnded={onVideoEnded}
            playsInline={true}
            poster={activeItem.videoThumbnail}
            preload="auto"
            ref={videoRef}
            src={activeItem.videoUrl}
          />
        ) : activeItem.image ? (
          <Image>
            <MobileAndTabletContainer style={{ height: "100%" }}>
              <ImageHelper
                imageProps={{
                  loading: "eager",
                  onLoad: onImageLoadMobile,
                  style: {
                    opacity,
                  },
                }}
                src={activeItem.mobileImage || activeItem.image}
              />
            </MobileAndTabletContainer>
            <DesktopOnlyContainer style={{ height: "100%" }}>
              <ImageHelper
                imageProps={{
                  loading: "eager",
                  onLoad: onImageLoadDesktop,
                  style: {
                    opacity,
                  },
                }}
                src={activeItem.image}
              />
            </DesktopOnlyContainer>
          </Image>
        ) : null}
        <MediaGradient />
      </MediaContainer>
      {!props.hideControls && (
        <>
          <ContentContainer>
            {props.items.map((item, index) => (
              <Item
                active={
                  activeItemIndex === index &&
                  carouselState == CarouselState.Playing
                }
                external={item.externalLink}
                itemCount={props.items.length}
                key={item.title + index}
                lastItem={props.items.length === index + 1}
                onClick={() => playSlide(index)}
                progressDuration={progressDuration}
                title={item.title}
                to={
                  item.link
                    ? item.externalLink
                      ? item.link
                      : formatLink(item.link)
                    : "/"
                }
              />
            ))}
          </ContentContainer>
          <PrevButton onClick={onPrev}>
            <Chevron strokeColor={colors.offWhite} strokeWidth="0.5" />
          </PrevButton>
          <NextButton onClick={onNext}>
            <Chevron strokeColor={colors.offWhite} strokeWidth="0.5" />
          </NextButton>
        </>
      )}
      {props.hideControls && (
        <Dots>
          {props.items.map((item, index) => (
            <Dot
              active={
                activeItemIndex === index &&
                carouselState == CarouselState.Playing
              }
              key={index}
              onClick={() => {
                if (index !== activeItemIndex) {
                  playSlide(index)
                }
              }}
            />
          ))}
        </Dots>
      )}
    </Container>
  )
}

export default MediaCarousel
